import { extractList } from './tournamentsApi';

export const EMPTY_PENDING_CAP_APPLICATIONS = {
  pending_cap_applications: 0,
  pending_cap_applications_by_champ: [],
};

const pendingCapPayload = (data) => {
  const root = data && typeof data === 'object' ? data : {};
  if (root.pending_cap_applications != null || Array.isArray(root.pending_cap_applications_by_champ)) {
    return root;
  }
  const nested = root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : null;
  if (nested && (nested.pending_cap_applications != null || Array.isArray(nested.pending_cap_applications_by_champ))) {
    return nested;
  }
  return null;
};

const champIdOfPendingRow = (item) => Number(
  item?.championship_id ?? item?.championat_id ?? item?.champ_id
);

export const extractPendingCapApplications = (data) => {
  const payload = pendingCapPayload(data);
  if (!payload) return null;

  const byChamp = (Array.isArray(payload.pending_cap_applications_by_champ)
    ? payload.pending_cap_applications_by_champ
    : [])
    .map((item) => ({
      championship_id: champIdOfPendingRow(item),
      count: Number(item?.count) || 0,
    }))
    .filter((item) => item.championship_id > 0);

  const totalRaw = Number(payload.pending_cap_applications);
  return {
    pending_cap_applications: Number.isFinite(totalRaw)
      ? totalRaw
      : byChamp.reduce((sum, item) => sum + item.count, 0),
    pending_cap_applications_by_champ: byChamp,
  };
};

export const pendingCapCountForChamp = (counts, champId) => {
  const byChamp = counts?.pending_cap_applications_by_champ || [];
  const parsed = Number(champId);
  if (Number.isFinite(parsed) && parsed > 0) {
    const row = byChamp.find((item) => item.championship_id === parsed);
    if (row) return row.count;
    if (byChamp.length) return 0;
  }
  if (counts?.pending_cap_applications != null) {
    return Number(counts.pending_cap_applications) || 0;
  }
  return byChamp.reduce((sum, item) => sum + item.count, 0);
};

export const formatPendingCapBadge = (count) => {
  const value = Number(count) || 0;
  if (value <= 0) return '';
  return value > 99 ? '99+' : String(value);
};

export const capApplicationId = (item) => {
  const id = Number(item?.cap_application_id ?? item?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const isCapApplicationFromBot = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (item.from_bot === true || item.from_bot === 1 || item.from_bot === '1') return true;
  return String(item.source || '').toLowerCase() === 'bot';};

export { isPlayerProfessional as isCapPlayerProfessional } from './playersApi';

export const extractCapTicket = (data) => {
  const payload = data?.application ? data : (data?.data || {});
  return {
    application: payload.application || {},
    players: extractList(payload.players),
    currentApplication: extractList(payload.current_application),
    copies: extractList(payload.copies),
  };
};

export const playerActionType = (status) => {
  const value = Number(status);
  if ([3, 4, 5].includes(value)) return 'remove';
  if ([6, 7, 8].includes(value)) return 'change';
  return 'add';
};

export const nextPlayerStatus = (oldStatus, action) => {
  const type = playerActionType(oldStatus);
  if (action === 'accept') {
    if (type === 'add') return 1;
    if (type === 'remove') return 4;
    return 7;
  }
  if (type === 'add') return 2;
  if (type === 'remove') return 5;
  return 8;
};

export const resetPlayerStatus = (status) => {
  const type = playerActionType(status);
  if (type === 'add') return 0;
  if (type === 'remove') return 3;
  return 6;
};

export const isPlayerPending = (status) => [0, 3, 6].includes(Number(status));
export const isPlayerAccepted = (status) => [1, 4, 7].includes(Number(status));
export const isPlayerDeclined = (status) => [2, 5, 8].includes(Number(status));

export const somePlayersDecided = (players) => (
  (players || []).some((player) => !isPlayerPending(player.status) && player.status != null)
);

export const playerFullName = (player) => (
  [player?.family, player?.name, player?.father_name].filter(Boolean).join(' ').trim()
);

export const formatCapDate = (value, locale) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const copyTeamName = (copy) => copy?.name || copy?.team_name || '—';
export const copyTeamImage = (copy) => copy?.image || copy?.team_image || '';

export const numWord = (value, words) => {
  const abs = Math.abs(Number(value) || 0) % 100;
  const num = abs % 10;
  if (abs > 10 && abs < 20) return words[2];
  if (num > 1 && num < 5) return words[1];
  if (num === 1) return words[0];
  return words[2];
};

export const playerWarningKey = (player, players, currentApplication) => {
  if (Number(player?.blacklisted) === 1 || player?.blacklisted === true) return 'blacklist';
  if (player?.black_list_id != null && player.black_list_id !== '') return 'blacklist';
  const status = Number(player.status);
  if (![0, 1, 2, 6, 7, 8].includes(status)) return null;

  const current = currentApplication || [];
  const number = player.player_number;

  if (player.player_id && current.some((item) => Number(item.id) === Number(player.player_id))) {
    return 'alreadyIn';
  }

  if (number != null && number !== '') {
    const duplicateInTicket = (players || []).some((item) => (
      item.id !== player.id
      && item.player_number != null
      && item.player_number !== ''
      && String(item.player_number) === String(number)
    ));
    if (duplicateInTicket) return 'sameNumber';

    const takenInRoster = current.some((item) => (
      item.number != null
      && String(item.number) === String(number)
      && Number(item.id) !== Number(player.player_id)
    ));
    if (takenInRoster) return 'numberTaken';
  }

  return null;
};

export const buildAcceptPayload = (application, players, copies) => {
  const teamIds = [];
  if (copies && copies.length) {
    teamIds.push(application.team_id);
    copies.forEach((copy) => {
      if (copy.team_id) teamIds.push(copy.team_id);
    });
  }

  const isAdd = (player) => [0, 1, 2].includes(Number(player.status));
  return {
    admin_comment: application.admin_comment || '',
    application_id: application.application_id,
    team_id: teamIds.length ? teamIds : application.team_id,
    team_name: application.team_name,
    created_id: application.created_id,
    newPlayers: players.filter((player) => isAdd(player) && !player.player_id),
    oldPlayerAdd: players.filter((player) => isAdd(player) && player.player_id),
    oldPlayersRemove: players.filter((player) => [3, 4, 5].includes(Number(player.status))),
    changePlayers: players.filter((player) => [6, 7, 8].includes(Number(player.status))),
  };
};

export const formatTeamNames = (value) => (
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(', ')
);

export const playerPositionLabel = (player) => (
  player?.short_position || player?.position || ''
);
